import * as THREE from 'three';

export interface TrackFrame {
  point: THREE.Vector3;
  tangent: THREE.Vector3;
  normal: THREE.Vector3;
  binormal: THREE.Vector3;
  distance: number;
}

export interface SampledTrack {
  curve: THREE.CatmullRomCurve3;
  frames: TrackFrame[];
  length: number;
  closed: boolean;
}

export const DEFAULT_POINTS = [
  new THREE.Vector3(-18, 2, -10),
  new THREE.Vector3(-10, 17, -8),
  new THREE.Vector3(0, 22, -2),
  new THREE.Vector3(13, 7, 2),
  new THREE.Vector3(16, 3, 14),
  new THREE.Vector3(2, 9, 18),
  new THREE.Vector3(-14, 4, 10),
];

function rotateAroundAxis(vector: THREE.Vector3, axis: THREE.Vector3, radians: number): THREE.Vector3 {
  return vector.applyAxisAngle(axis, radians).normalize();
}

/** Parallel-transport frames avoid the sudden normal flips common in naive lookAt track frames. */
export function sampleTrack(points: THREE.Vector3[], segments = 240, closed = true): SampledTrack {
  const curve = new THREE.CatmullRomCurve3(points, closed, 'centripetal', 0.45);
  const length = curve.getLength();
  const frames: TrackFrame[] = [];
  let lastTangent = curve.getTangentAt(0).normalize();
  const seedUp = Math.abs(lastTangent.dot(new THREE.Vector3(0, 1, 0))) > 0.94
    ? new THREE.Vector3(1, 0, 0)
    : new THREE.Vector3(0, 1, 0);
  let normal = seedUp.clone().sub(lastTangent.clone().multiplyScalar(seedUp.dot(lastTangent))).normalize();

  for (let i = 0; i <= segments; i += 1) {
    const u = i / segments;
    const tangent = curve.getTangentAt(u).normalize();
    const axis = new THREE.Vector3().crossVectors(lastTangent, tangent);
    if (axis.lengthSq() > 1e-10) {
      axis.normalize();
      const angle = Math.acos(THREE.MathUtils.clamp(lastTangent.dot(tangent), -1, 1));
      normal = rotateAroundAxis(normal, axis, angle);
    }
    // A procedural bank proves the frame can roll without renderer-specific state.
    const bank = Math.sin(u * Math.PI * 4) * THREE.MathUtils.degToRad(22);
    const bankedNormal = rotateAroundAxis(normal.clone(), tangent, bank);
    const binormal = new THREE.Vector3().crossVectors(tangent, bankedNormal).normalize();
    frames.push({
      point: curve.getPointAt(u),
      tangent,
      normal: bankedNormal,
      binormal,
      distance: length * u,
    });
    lastTangent = tangent;
  }

  return { curve, frames, length, closed };
}

export function frameAtDistance(track: SampledTrack, distance: number): TrackFrame {
  const wrapped = track.closed
    ? ((distance % track.length) + track.length) % track.length
    : THREE.MathUtils.clamp(distance, 0, track.length);
  const position = (wrapped / track.length) * (track.frames.length - 1);
  const a = Math.floor(position);
  const b = Math.min(track.frames.length - 1, a + 1);
  const t = position - a;
  const first = track.frames[a];
  const second = track.frames[b];
  const tangent = first.tangent.clone().lerp(second.tangent, t).normalize();
  const normal = first.normal.clone().lerp(second.normal, t).normalize();
  return {
    point: first.point.clone().lerp(second.point, t),
    tangent,
    normal,
    binormal: new THREE.Vector3().crossVectors(tangent, normal).normalize(),
    distance: wrapped,
  };
}

export function makeRailGeometry(track: SampledTrack, radius = 0.14): THREE.BufferGeometry {
  const railOffsets = [-0.62, 0.62];
  const vertices: number[] = [];
  const indices: number[] = [];
  const radialSegments = 6;
  for (const railOffset of railOffsets) {
    const base = vertices.length / 3;
    for (const frame of track.frames) {
      const center = frame.point.clone().addScaledVector(frame.binormal, railOffset);
      for (let r = 0; r < radialSegments; r += 1) {
        const angle = (r / radialSegments) * Math.PI * 2;
        const point = center.clone()
          .addScaledVector(frame.normal, Math.cos(angle) * radius)
          .addScaledVector(frame.binormal, Math.sin(angle) * radius);
        vertices.push(point.x, point.y, point.z);
      }
    }
    for (let row = 0; row < track.frames.length - 1; row += 1) {
      for (let r = 0; r < radialSegments; r += 1) {
        const next = (r + 1) % radialSegments;
        const a = base + row * radialSegments + r;
        const b = base + row * radialSegments + next;
        const c = base + (row + 1) * radialSegments + r;
        const d = base + (row + 1) * radialSegments + next;
        indices.push(a, c, b, b, c, d);
      }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}
